import { Download, FileText, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

export interface EvidenceFile {
  filename: string;
  url?: string;
}

interface ReportData {
  adverseMedia?: {
    evidence?: Array<{
      filename?: string;
      url?: string;
      metadata?: { filename?: string };
    }>;
    evidencePdfFiles?: Array<{
      filename?: string;
      url?: string;
      pdfPath?: string;
    }>;
  };
  pepEvidence?: Array<{
    filename?: string;
    url?: string;
    pdfPath?: string;
  }>;
  evidenceFiles?: Array<{ filename: string; url?: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Extract evidence files from reportData, checking multiple possible locations.
 */
export function getEvidenceFiles(reportData: ReportData): EvidenceFile[] {
  const files: EvidenceFile[] = [];

  // Issue 5: adverseMedia.evidencePdfFiles is the full set of captured PDFs
  // (post-AI-validation candidates, not just relevant ones). Prefer it; fall
  // back to .evidence for legacy assessments saved before evidencePdfFiles
  // was populated.
  if (reportData.adverseMedia?.evidencePdfFiles) {
    for (const item of reportData.adverseMedia.evidencePdfFiles) {
      if (item.filename) {
        files.push({ filename: item.filename, url: item.url });
      }
    }
  } else if (reportData.adverseMedia?.evidence) {
    for (const item of reportData.adverseMedia.evidence) {
      if (item.filename) {
        files.push({ filename: item.filename, url: item.url });
      }
      if (item.metadata?.filename) {
        files.push({
          filename: item.metadata.filename as string,
          url: item.url,
        });
      }
    }
  }

  // Issue 5: PEP article PDF snapshots (EDD only).
  if (reportData.pepEvidence) {
    for (const item of reportData.pepEvidence) {
      if (item.filename) {
        files.push({ filename: item.filename, url: item.url });
      }
    }
  }

  // Check Companies House officer matches for PDF evidence
  // Note: Try both camelCase (from screening service) and snake_case (from investigator)
  const companiesHouseOfficers =
    (reportData as Record<string, unknown>).companiesHouseOfficers ||
    reportData.companies_house_officers;
  if (companiesHouseOfficers) {
    for (const officer of companiesHouseOfficers as Array<{
      evidence?: { pdfPath?: string; sourceUrl?: string };
      officer?: { name?: string };
      company?: { name?: string };
    }>) {
      if (officer.evidence?.pdfPath) {
        const filename = officer.evidence.pdfPath.split('/').pop();
        if (filename) {
          files.push({
            filename,
            url: officer.evidence.sourceUrl,
          });
        }
      }
    }
  }

  // Check for evidence files stored at the top level
  if (reportData.evidenceFiles) {
    for (const file of reportData.evidenceFiles as EvidenceFile[]) {
      if (file.filename && !files.some((f) => f.filename === file.filename)) {
        files.push(file);
      }
    }
  }

  // Deduplicate by filename
  const seen = new Set<string>();
  return files.filter((file) => {
    if (seen.has(file.filename)) return false;
    seen.add(file.filename);
    return true;
  });
}

interface EvidencePackageSectionProps {
  files: EvidenceFile[];
  onDownload: (filename: string) => void;
  onDownloadAll: () => void;
  isDownloadingAll: boolean;
}

type EvidenceCategory = 'adverseMedia' | 'pep' | 'companiesHouse' | 'other';

interface CategoryDescriptor {
  key: EvidenceCategory;
  label: string;
}

const CATEGORY_ORDER: CategoryDescriptor[] = [
  { key: 'adverseMedia', label: 'Adverse Media' },
  { key: 'pep', label: 'PEP' },
  { key: 'companiesHouse', label: 'Companies House' },
  { key: 'other', label: 'Other' },
];

/**
 * Categorize a file by its filename prefix.
 * - {caseId}_media_snapshot_N.pdf → adverse media
 * - {caseId}_pep_snapshot_N.pdf   → PEP
 * - {caseId}_officer_N.pdf         → Companies House officer
 */
function categorizeFile(filename: string): EvidenceCategory {
  if (filename.includes('_pep_snapshot_')) return 'pep';
  if (filename.includes('_media_snapshot_')) return 'adverseMedia';
  if (filename.includes('_officer_') || filename.includes('_company_')) return 'companiesHouse';
  return 'other';
}

function groupFilesByCategory(files: EvidenceFile[]): Map<EvidenceCategory, EvidenceFile[]> {
  const groups = new Map<EvidenceCategory, EvidenceFile[]>();
  for (const file of files) {
    const category = categorizeFile(file.filename);
    const existing = groups.get(category) ?? [];
    existing.push(file);
    groups.set(category, existing);
  }
  // Stable sort within each group: lexicographic by filename — keeps
  // numbered snapshots in ascending order (snapshot_1, snapshot_2, ...).
  for (const list of groups.values()) {
    list.sort((a, b) =>
      a.filename.localeCompare(b.filename, undefined, { numeric: true, sensitivity: 'base' }),
    );
  }
  return groups;
}

export function EvidencePackageSection({
  files,
  onDownload,
  onDownloadAll,
  isDownloadingAll,
}: EvidencePackageSectionProps) {
  const grouped = groupFilesByCategory(files);

  return (
    <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[#051923]">Evidence Package</h2>
        {files.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="text-sm"
            onClick={onDownloadAll}
            disabled={isDownloadingAll}
          >
            {isDownloadingAll ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {isDownloadingAll ? 'Downloading...' : 'Download All'}
          </Button>
        )}
      </div>
      {files.length > 0 ? (
        <>
          <p className="mb-4 text-sm text-gray-600">
            Supporting documents collected during screening, grouped by check type. Includes PDF
            snapshots of adverse media articles, PEP source articles, and Companies House officer
            appointment records.
          </p>
          <div className="space-y-6">
            {CATEGORY_ORDER.map((descriptor) => {
              const groupFiles = grouped.get(descriptor.key);
              if (!groupFiles || groupFiles.length === 0) return null;
              return (
                <div key={descriptor.key}>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
                    {descriptor.label}{' '}
                    <span className="ml-1 text-xs font-normal text-gray-500">
                      ({groupFiles.length})
                    </span>
                  </h3>
                  <ul className="space-y-1">
                    {groupFiles.map((file) => (
                      <li
                        key={file.filename}
                        className="flex items-center justify-between gap-4 py-2"
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <FileText className="h-5 w-5 shrink-0 text-gray-400" />
                          <span className="break-all text-sm">{file.filename}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => onDownload(file.filename)}
                          className="flex shrink-0 items-center gap-1 p-2 text-sm text-blue-600 hover:underline"
                        >
                          <Download className="h-5 w-5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500">
          No evidence files were generated for this assessment. PDF snapshots of source articles are
          captured when adverse media screening finds relevant results in Enhanced Due Diligence
          mode.
        </p>
      )}
    </section>
  );
}
